// src/hooks/useThemePalette.ts
// Returns a typed colour palette object that flips between dark and light
// themes. Import this in every component that has inline styles — replace
// the static `const C = { ... }` block with `const C = useThemePalette()`.

import { useThemeStore } from '../stores/useThemeStore';

export interface Palette {
  bg:       string;
  surface:  string;
  card:     string;
  border:   string;
  borderH:  string;
  accent:   string;
  accent2:  string;
  accent3:  string;
  success:  string;
  text:     string;
  muted:    string;
  purple:   string;
  error:    string;
  premium:  string;
  // Extra semantic tokens for overlays / frosted glass
  overlay:  string; // gradient overlay on hero images
  frosted:  string; // frosted glass card bg
  navBg:    string; // navbar background (scrolled)
  navPill:  string; // center nav pill background
}

const dark: Palette = {
  bg:       '#080c14',
  surface:  '#0d1220',
  card:     'rgba(255,255,255,0.04)',
  border:   'rgba(255,255,255,0.07)',
  borderH:  'rgba(255,255,255,0.12)',
  accent:   '#4f8cf7',
  accent2:  '#6366f1',
  accent3:  '#a855f7',
  success:  '#34d399',
  text:     '#f1f5f9',
  muted:    '#8b9cc4',
  purple:   '#4f3ff5',
  error:    '#f87171',
  premium:  '#fbbf24',
  overlay:  'rgba(8,12,20,0.88)',
  frosted:  'rgba(13,18,32,0.60)',
  navBg:    'rgba(8,12,20,0.96)',
  navPill:  'rgba(255,255,255,0.05)',
};

const light: Palette = {
  bg:       '#f8fafc',
  surface:  '#ffffff',
  card:     '#ffffff',
  border:   '#e2e8f0',
  borderH:  '#cbd5e1',
  accent:   '#4f8cf7',
  accent2:  '#6366f1',
  accent3:  '#a855f7',
  success:  '#10b981',
  text:     '#1e293b',
  muted:    '#64748b',
  purple:   '#4f3ff5',
  error:    '#ef4444',
  premium:  '#d97706',
  overlay:  'rgba(248,250,252,0.82)',
  frosted:  'rgba(255,255,255,0.80)',
  navBg:    'rgba(255,255,255,0.98)',
  navPill:  'rgba(0,0,0,0.04)',
};

export function useThemePalette(): Palette {
  const theme = useThemeStore((s) => s.theme);
  return theme === 'light' ? light : dark;
}